import React, { useMemo, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtText from "../../components/NtText";
import NtTextInput from "../../components/NtTextInput";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtMaterialIcon from "../../components/NtMaterialIcon";
import { useTheme } from "../../context/ThemeContext";

/**
 * Onboarding input field with attached error strip. Always renders the same
 * tree so the inner TextInput never remounts — toggling the error display
 * doesn't steal focus mid-typing.
 *
 * Error visibility follows the Stripe / Linear pattern:
 *   - Hidden while the field is focused (user is actively typing/fixing).
 *   - Hidden until the user has blurred the field at least once.
 *   - Shown once `touched && !focused && !!error`.
 *
 * Props mirror NtModalInputField: title, footer, placeholder, value,
 * onChangeText, plus ...rest forwarded to NtTextInput.
 */
function NtOnboardingField({
  title,
  error,
  footer,
  placeholder,
  value,
  onChangeText,
  onFocus,
  onBlur,
  ...rest
}) {
  const { theme } = useTheme();
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  const showError = touched && !focused && !!error;
  const styles = useMemo(() => makeStyles(theme, showError), [theme, showError]);

  const handleFocus = (e) => {
    setFocused(true);
    if (onFocus) onFocus(e);
  };
  const handleBlur = (e) => {
    setFocused(false);
    setTouched(true);
    if (onBlur) onBlur(e);
  };

  return (
    <View style={styles.container}>
      {title ? (
        <NtModalInputTitle style={styles.title}>{title}</NtModalInputTitle>
      ) : null}
      <View style={styles.wrap}>
        <NtTextInput
          containerStyle={styles.input}
          placeholder={placeholder}
          value={value}
          autoCorrect={false}
          autoCapitalize="none"
          onChangeText={onChangeText}
          onFocus={handleFocus}
          onBlur={handleBlur}
          borderColor="transparent"
          {...rest}
        />
        {showError ? (
          <View style={styles.strip}>
            <NtMaterialIcon name="alert-circle" size={13} color="#ffffff" />
            <NtText style={styles.stripText}>{error}</NtText>
          </View>
        ) : null}
      </View>
      {footer && !showError ? (
        <NtText style={styles.footer}>{footer}</NtText>
      ) : null}
    </View>
  );
}

const makeStyles = (t, hasError) =>
  StyleSheet.create({
    container: { marginBottom: 8 },
    title: { marginLeft: 4, marginBottom: 4 },
    wrap: {
      borderWidth: 1,
      borderColor: hasError ? t.red : t.borderSoft,
      borderRadius: t.radius,
      backgroundColor: t.surface2,
      overflow: "hidden",
    },
    // The inner input drops its own border + radius since the wrap owns those.
    // height: 38 matches NtModalInputField's standard input height.
    input: {
      borderWidth: 0,
      borderRadius: 0,
      backgroundColor: "transparent",
      height: 38,
    },
    strip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      paddingVertical: 6,
      paddingHorizontal: 12,
      backgroundColor: t.red,
    },
    stripText: {
      flex: 1,
      fontSize: 11,
      fontWeight: "600",
      color: "#ffffff",
      letterSpacing: 0.1,
    },
    footer: {
      marginTop: 4,
      marginLeft: 4,
      fontSize: 11,
      color: t.textMute,
      fontStyle: "italic",
    },
  });

export default NtOnboardingField;
